import { Button } from '@creditornot/cb-button';
import { Plus } from '@creditornot/cb-icons';
import { Spinner } from '@creditornot/cb-progress';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRouteMatch, Route, Switch } from 'react-router';
import { useTheme } from 'styled-components';

import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { APIErrorNotification } from 'common/components';
import { Root, SpinnerContainer } from 'modules/shared-menu/components/CommonStyles';
import { HeaderContainer, Header } from 'modules/shared-menu/components/CommonStyles';
import { CreateSelectionModal } from 'modules/shared-menu/components/CreateSelectionModal';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { useListingManagerImageGallery } from 'modules/shared-menu/hooks/useListingManagerImageGallery';
import { useSharedMenuData } from 'modules/shared-menu/hooks/useSharedMenuData';
import useVenuesWithSelectionLinks from 'modules/shared-menu/hooks/useVenuesWithSelectionLinks';
import { SharedMenu } from 'modules/shared-menu/types';

import { SelectionList } from './components/SelectionList';
import { UnassignedVenuesInfo } from './components/UnassignedVenuesInfo';
import { UnassignedVenuesSidepanel } from './components/UnassignedVenuesSidepanel';
import { VenueManagementSidepanel } from './components/VenueManagementSidepanel';
import { SharedMenuRoot } from '../SharedMenuPage';

type SharedMenuRootPageProps = {
  country: Country;
  venues: Venue[];
};

export const SharedMenuRootPageV2 = ({ country, venues }: SharedMenuRootPageProps) => {
  const { cbDirection } = useTheme();
  const { path } = useRouteMatch();
  const scope = useScopeContext();

  const [activeSelection, setActiveSelection] = useState<SharedMenu | undefined>(undefined);
  const [isUnassignedVenuesSidepanelOpen, setIsUnassignedVenuesSidepanelOpen] = useState(false);
  const [isVenueManagementSidepanelOpen, setIsVenueManagementSidepanelOpen] = useState(false);
  const [isCreateSelectionModalOpen, setIsCreateSelectionModalOpen] = useState(false);

  const { sharedMenus, loadingSharedMenus, sharedMenusError } = useSharedMenuData({ scope });

  const {
    venues: venuesWithSelectionLinks,
    loading: loadingVenuesWithSelectionLinks,
    error: venuesWithSelectionLinksError,
  } = useVenuesWithSelectionLinks({
    venues,
    sharedMenus,
    currentScopeType: scope.type,
  });

  const { isLoading: loadingSharedMenuImageGallery, error: sharedMenuImagesError } = useListingManagerImageGallery();

  const loading = loadingSharedMenus || loadingVenuesWithSelectionLinks || loadingSharedMenuImageGallery;
  const error = sharedMenusError || venuesWithSelectionLinksError || sharedMenuImagesError;

  const venuesNotLinkedToAnySharedMenu = useMemo(() => {
    return venuesWithSelectionLinks.filter(venue => venue.sharedMenuLink.scope === 'none');
  }, [venuesWithSelectionLinks]);

  if (loading) {
    return (
      <Root dir={cbDirection}>
        <SpinnerContainer>
          <Spinner size="large" />
        </SpinnerContainer>
      </Root>
    );
  }

  return (
    <Root dir={cbDirection}>
      {error && <APIErrorNotification error={error} style={{ margin: '24px 48px' }} />}

      <Switch>
        <Route path={path} exact>
          <HeaderContainer>
            <Header>
              <FormattedMessage id="listing-manager.page-header" />
            </Header>
            <div>
              <Button icon={<Plus />} variant="blue" size="small" onClick={() => setIsCreateSelectionModalOpen(true)}>
                <FormattedMessage id="listing-manager.create-menu.button-label" />
              </Button>
            </div>
          </HeaderContainer>

          {venuesNotLinkedToAnySharedMenu.length > 0 && (
            <UnassignedVenuesInfo
              onClickShowVenues={() => setIsUnassignedVenuesSidepanelOpen(true)}
              unassignedVenues={venuesNotLinkedToAnySharedMenu}
            />
          )}

          <SelectionList
            country={country}
            onEditSelectionVenues={selection => {
              setActiveSelection(selection);
              setIsVenueManagementSidepanelOpen(true);
            }}
            selections={sharedMenus}
            venuesWithSelectionLinks={venuesWithSelectionLinks}
          />
        </Route>

        {sharedMenus.map(sharedMenu => (
          <Route path={`${path}/${sharedMenu.id}`} key={sharedMenu.id}>
            <SharedMenuRoot country={country} allVenues={venuesWithSelectionLinks} sharedMenu={sharedMenu} />
          </Route>
        ))}
      </Switch>

      <CreateSelectionModal
        onClose={() => setIsCreateSelectionModalOpen(false)}
        isOpen={isCreateSelectionModalOpen}
        country={country}
      />

      {isUnassignedVenuesSidepanelOpen && (
        <UnassignedVenuesSidepanel
          venues={venuesNotLinkedToAnySharedMenu}
          onClose={() => setIsUnassignedVenuesSidepanelOpen(false)}
        />
      )}

      {isVenueManagementSidepanelOpen && (
        <VenueManagementSidepanel
          allVenues={venuesWithSelectionLinks}
          onClose={() => setIsVenueManagementSidepanelOpen(false)}
          selection={activeSelection}
        />
      )}
    </Root>
  );
};
